import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, Package as PackageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function ProductForm({ open, onClose, onSubmit, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    product_id: "", name: "", category: "Ready-to-Eat",
    net_weight_g: "", rehydrated_weight_g: "", mrp: "", hsn: "21069099",
    rm_cost: "", packing_type: "Tub",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {

      // eslint-disable-next-line
      setForm({
        product_id: initial.product_id || "",
        name: initial.name || "",
        category: initial.category || "Ready-to-Eat",
        net_weight_g: initial.net_weight_g ?? "",
        rehydrated_weight_g: initial.rehydrated_weight_g ?? "",
        mrp: initial.mrp ?? "",
        hsn: initial.hsn || "21069099",
        rm_cost: initial.rm_cost ?? "",
        packing_type: initial.packing_type || "Tub",
      });
    } else {
      setForm({ product_id: "", name: "", category: "Ready-to-Eat",
                net_weight_g: "", rehydrated_weight_g: "", mrp: "", hsn: "21069099",
                rm_cost: "", packing_type: "Tub" });
    }
  }, [initial, open]);

  const submit = async () => {
    if (!form.name.trim() || !form.product_id.trim()) {
      toast.error("Product ID and Name required"); return;
    }
    const payload = {
      ...form,
      net_weight_g: parseFloat(form.net_weight_g) || 0,
      rehydrated_weight_g: parseFloat(form.rehydrated_weight_g) || 0,
      mrp: parseFloat(form.mrp) || 0,
      rm_cost: parseFloat(form.rm_cost) || 0,
    };
    setSaving(true);
    try { await onSubmit(payload); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-xl" data-testid="product-form">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Product" : "New Product"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            MRP is the price including 5% GST.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Product ID</Label>
            <Input value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="prod-id" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">HSN</Label>
            <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="mt-1.5" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Category</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">MRP (₹, incl. 5% GST)</Label>
            <Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="prod-mrp" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Net Weight (g)</Label>
            <Input type="number" value={form.net_weight_g} onChange={(e) => setForm({ ...form, net_weight_g: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Rehydrated Weight (g)</Label>
            <Input type="number" value={form.rehydrated_weight_g} onChange={(e) => setForm({ ...form, rehydrated_weight_g: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">RM Cost (₹)</Label>
            <Input type="number" value={form.rm_cost} onChange={(e) => setForm({ ...form, rm_cost: e.target.value })} data-testid="prod-rm-cost" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Packing Type</Label>
            <select
              value={form.packing_type}
              onChange={(e) => setForm({ ...form, packing_type: e.target.value })}
              className="mt-1.5 h-10 w-full px-3 border border-slate-200 rounded-md bg-white text-sm"
              data-testid="prod-packing-type"
            >
              <option value="Tub">Tub (510 ml)</option>
              <option value="Pouch">Pouch (50 g)</option>
              <option value="HoReCa">HoReCa (250 g brick)</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60" data-testid="prod-save">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { search: search || undefined } });
      setRows(data);
    } catch (err) {
      console.error("Failed to load products:", err);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const submit = async (payload) => {
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setShowForm(false); setEditing(null); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="products-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Inventory</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Products</h1>
          </div>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-product-button" className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
            <Plus size={16} weight="bold" /> New Product
          </button>
        </div>
      </div>
      <div className="p-8 space-y-5">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or product ID…" className="pl-9 h-10 bg-white border-slate-200 max-w-md" data-testid="product-search" />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-6 py-3">Product</th>
                <th className="px-6 py-3">Category</th>
                <th className="px-6 py-3">Net Wt.</th>
                <th className="px-6 py-3">Rehydrated</th>
                <th className="px-6 py-3 text-right">MRP (₹)</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (<tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">No products</td></tr>)}
              {!loading && rows.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-md flex items-center justify-center text-slate-500"><PackageIcon size={16} weight="duotone" /></div>
                      <div>
                        <div className="font-medium text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.product_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{p.category}</td>
                  <td className="px-6 py-4 text-slate-700">{p.net_weight_g ? `${p.net_weight_g} g` : "—"}</td>
                  <td className="px-6 py-4 text-slate-700">{p.rehydrated_weight_g ? `${p.rehydrated_weight_g} g` : "—"}</td>
                  <td className="px-6 py-4 text-slate-900 font-semibold text-right">₹{Number(p.mrp).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 rounded-md text-slate-500 hover:text-blue-600 hover:bg-slate-100" data-testid={`edit-product-${p.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => remove(p)} className="p-2 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-product-${p.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showForm && (
        <ProductForm open={showForm} initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSubmit={submit} />
      )}
    </div>
  );
}
